<template>
  <div>
    <div :id="id" :style="{ height: height - 32 + 'px' }" />
    <NoDataOverlay :model-value="!(value && value.length)" />
  </div>
</template>
  
  <script>
import * as echarts from "echarts";
import themeJson from "@/assets/echarts-themes/oceanic-balanced.json";
import { v4 as uuidv4 } from "uuid";
import moment from "moment";
import NoDataOverlay from "@/components/atoms/analytics/NoDataOverlay.vue";

export default {
  name: "CampaignDaily",
  components: {
    NoDataOverlay,
  },
  props: {
    id: {
      type: String,
      default: `campaign-daily-${uuidv4()}`,
    },
    value: {
      type: Array,
      default: () => [],
    },
    height: {
      type: Number,
      default: 300,
    },
  },
  data() {
    return {
      chartInstance: null,
      resizeObserver: null,
    };
  },
  watch: {
    value: {
      handler() {
        this.renderChart();
      },
      deep: true,
    },
  },
  mounted() {
    this.renderChart();
    this.observeResize();
  },
  beforeUnmount() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.chartInstance) {
      this.chartInstance.dispose();
    }
  },

  methods: {
    getDailyLabelsFromPast7Days() {
      const start = moment().subtract(6, "days").startOf("day");
      const labels = [];

      for (let i = 0; i < 7; i++) {
        const date = start.clone().add(i, "days");
        labels.push(date.format("MMMM D"));
      }

      return labels;
    },
    renderChart() {
      const chartDom = document.getElementById(this.id);
      if (!chartDom) return;

      const chart = echarts.init(chartDom, themeJson.themeName);
      chart.setOption(this.getOptions());

      this.chartInstance = chart;
    },
    observeResize() {
      const chartDom = document.getElementById(this.id);
      if (!chartDom) return;

      this.resizeObserver = new ResizeObserver(() => {
        requestAnimationFrame(() => {
          this.chartInstance?.resize();
        });
      });

      this.resizeObserver.observe(chartDom);
    },
    getOptions() {
      return {
        title: {
          text: "Daily Performance",
          left: "center",
        },
        tooltip: {
          trigger: "axis",
          confine: true,
          axisPointer: {
            type: "cross",
            label: {
              backgroundColor: "#6a7985",
            },
          },
        },
        legend: {
          data: ["Impressions", "Views", "Clicks", "Visits", "Conversions"],
          bottom: 0,
          left: "center",
        },
        toolbox: {
          feature: {
            saveAsImage: {},
          },
        },
        grid: {
          left: "3%",
          right: "4%",
          bottom: "10%",
          containLabel: true,
        },
        xAxis: [
          {
            type: "category",
            boundaryGap: false,
            data: this.getDailyLabelsFromPast7Days(),
          },
        ],
        yAxis: [
          {
            type: "value",
          },
        ],
        series: [
          {
            name: "Impressions",
            type: "line",
            stack: "Total",
            areaStyle: {},
            emphasis: {
              focus: "series",
            },
            data: this.getDailyLabelsFromPast7Days().map(
              (el) =>
                this.value.find((e) => moment(e.date).format("MMMM D") === el)
                  ?.impressions || 0
            ),
          },
          {
            name: "Views",
            type: "line",
            stack: "Total",
            areaStyle: {},
            emphasis: {
              focus: "series",
            },
            data: this.getDailyLabelsFromPast7Days().map(
              (el) =>
                this.value.find((e) => moment(e.date).format("MMMM D") === el)
                  ?.views || 0
            ),
          },
          {
            name: "Clicks",
            type: "line",
            stack: "Total",
            areaStyle: {},
            emphasis: {
              focus: "series",
            },
            data: this.getDailyLabelsFromPast7Days().map(
              (el) =>
                this.value.find((e) => moment(e.date).format("MMMM D") === el)
                  ?.clicks || 0
            ),
          },
          {
            name: "Visits",
            type: "line",
            stack: "Total",
            areaStyle: {},
            emphasis: {
              focus: "series",
            },
            data: this.getDailyLabelsFromPast7Days().map(
              (el) =>
                this.value.find((e) => moment(e.date).format("MMMM D") === el)
                  ?.visits || 0
            ),
          },
          {
            name: "Conversions",
            type: "line",
            stack: "Total",
            areaStyle: {},
            emphasis: {
              focus: "series",
            },
            data: this.getDailyLabelsFromPast7Days().map(
              (el) =>
                this.value.find((e) => moment(e.date).format("MMMM D") === el)
                  ?.conversions || 0
            ),
          },
        ],
      };
    },
  },
};
</script>
  
  <style scoped>
.inventory-item-pie-chart {
  width: 100%;
  font-family: Roboto, sans-serif;
}
</style>
  